import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterMelkbosstrand() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-melkbosstrand",
    name: "Painters Melkbosstrand",
    location: "Melkbosstrand, Cape Town",
    rating: 4.9,
    reviews: 142,
    specialties: ["Interior Painting", "Exterior Painting", "Coastal Properties", "Holiday Homes"],
    image: "https://public.readdy.ai/ai/img_res/f2de0c51be9fa120e1aa201b0b89b8b6.jpg",
    verified: true,
    yearsExperience: 15,
    website: "https://paintersmelkbosstrand.co.za",
    email: "info@paintersmelkbosstrand.co.za",
    phone: "+27 21 555 0125",
    description: "Professional painting services in Melkbosstrand and surrounding coastal areas. We specialize in holiday homes and coastal properties, with expertise in weather-resistant finishes that protect against salt air and coastal conditions.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Holiday Home Painting",
      "Roof Painting",
      "Waterproofing",
      "Damp Proofing",
      "Wall Preparation",
      "Color Consultation"
    ],
    serviceAreas: ["Melkbosstrand", "Bloubergstrand", "Atlantis", "West Coast", "Coastal Areas", "Holiday Destinations"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Melkbosstrand%2C%20modern%20coastal%20holiday%20home%20with%20fresh%20white%20walls%2C%20quality%20paint%20finish%2C%20ocean%20view%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=melkbosstrand-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Melkbosstrand%20Cape%20Town%2C%20coastal%20holiday%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20beachfront%20setting%2C%20well-maintained%20property&width=400&height=300&seq=melkbosstrand-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Holiday%20home%20painting%20project%20in%20Melkbosstrand%2C%20professional%20painters%20working%20on%20vacation%20property%20exterior%2C%20quality%20residential%20paint%20finish%2C%20modern%20coastal%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=melkbosstrand-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Bathroom%20interior%20painting%20in%20Melkbosstrand%20coastal%20home%2C%20fresh%20painted%20walls%2C%20modern%20bathroom%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=melkbosstrand-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Robert Wilson",
        rating: 5,
        comment: "Great work on our holiday home. The painters understood the coastal challenges and delivered a finish that looks fantastic and protects against the elements."
      },
      {
        name: "Jennifer Adams", 
        rating: 4,
        comment: "Professional service and good quality work. They completed our beach house painting on time and within budget."
      },
      {
        name: "Peter Jacobs",
        rating: 5,
        comment: "Excellent painters for coastal properties. They know how to handle the unique challenges of painting near the ocean."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Melkbosstrand | Professional Coastal Painting Services Cape Town | Beachfront Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Melkbosstrand offers professional painting services for coastal properties in Melkbosstrand, Cape Town. Specializing in beachfront homes, marine-grade finishes, and holiday properties. Expert coastal painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Melkbosstrand, house painters Melkbosstrand, coastal painting contractors Cape Town, beachfront property painting, marine-grade finishes');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${painter.image})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Melkbosstrand - Your Premier West Coast Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Painters Melkbosstrand stands as the premier painting contractor serving the beautiful West Coast region of Cape Town. With over 15 years of specialized experience in coastal painting, we have established ourselves as the trusted choice for homeowners, holiday property investors, and commercial clients throughout Melkbosstrand and surrounding areas. Our deep understanding of coastal conditions, combined with our commitment to excellence, makes us the ideal partner for all your painting needs.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Professional Painting Services in Melkbosstrand</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Located in one of Cape Town's most sought-after coastal destinations, Melkbosstrand presents unique challenges for painting projects. The combination of salt air, strong coastal winds, and intense UV radiation requires specialized knowledge and premium materials. Our team has mastered these challenges, developing proven techniques and using only the highest quality marine-grade paints and protective coatings that ensure your property maintains its beauty and value for years to come.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Holiday Home and Beachfront Property Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Melkbosstrand is renowned for its stunning holiday homes and beachfront properties, and we specialize in maintaining these valuable investments. Our holiday home painting services are designed around the unique needs of seasonal properties, including flexible scheduling to accommodate rental periods, rapid project completion to minimize disruption, and long-lasting finishes that maintain their appearance between visits. We understand that your holiday home is both a personal retreat and a financial investment, and our work reflects this dual responsibility.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Advanced Coastal Protection Systems</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our coastal protection systems go beyond standard painting services. We employ multi-layer coating systems specifically designed for marine environments, including specialized primers that bond effectively to substrates exposed to salt air, intermediate coats that provide UV protection and flexibility, and topcoats formulated to resist fading, chalking, and salt damage. This comprehensive approach ensures that your property not only looks exceptional but also receives maximum protection against the harsh coastal environment.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-8">Comprehensive Painting Services in Melkbosstrand</h2>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Painting Excellence</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our residential painting services encompass every aspect of home beautification and protection. From intimate beach cottages to expansive coastal estates, we approach each project with the same attention to detail and commitment to quality. Our interior painting services transform living spaces into comfortable, stylish environments that reflect your personal taste while considering the coastal lifestyle. We use low-VOC paints that are safe for families and pets, and our color consultation services help you choose palettes that complement both your home's architecture and the stunning natural surroundings.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exterior Protection and Enhancement</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Exterior painting in Melkbosstrand requires more than aesthetic considerations – it's about protecting your investment from the elements. Our exterior painting process begins with comprehensive surface preparation, including pressure washing, scraping, sanding, and priming as needed. We pay special attention to areas most vulnerable to coastal damage, such as window frames, door surrounds, and trim work. Our painters are trained in proper application techniques for windy conditions, ensuring even coverage and optimal adhesion regardless of weather challenges.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Specialized Waterproofing and Damp Solutions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Coastal properties face unique moisture challenges, from salt-laden air to occasional storm exposure. Our waterproofing services provide comprehensive protection against water intrusion, including specialized sealants for joints and penetrations, breathable wall coatings that allow moisture vapor to escape while preventing liquid water entry, and advanced damp-proofing systems for basements and ground-level areas. These services are essential for maintaining the structural integrity and comfort of coastal properties.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Assurance and Customer Satisfaction</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our commitment to quality extends beyond the completion of each project. We provide comprehensive warranties on all our work, backed by our confidence in our materials and techniques. Our quality assurance process includes regular inspections during the painting process, final walk-throughs with clients to ensure complete satisfaction, and follow-up services to address any concerns. We maintain detailed records of all work performed, including paint specifications and application dates, to facilitate future maintenance and warranty claims.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Environmental Responsibility and Community Commitment</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  As a local Melkbosstrand business, we are committed to protecting the beautiful coastal environment that makes our community special. We use environmentally responsible painting products whenever possible, properly dispose of all waste materials, and follow best practices to minimize our environmental impact. Our team members are local residents who understand and appreciate the unique character of Melkbosstrand, and this personal connection to the community drives our commitment to excellence in every project we undertake.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20beautiful%20coastal%20house%20in%20Melkbosstrand%20South%20Africa%2C%20painters%20in%20uniform%20with%20quality%20equipment%20and%20brushes%2C%20modern%20coastal%20architecture%20with%20ocean%20views%2C%20bright%20sunny%20day%2C%20organized%20work%20site%20with%20professional%20painting%20tools&width=400&height=300&seq=melkbosstrand-sidebar-image&orientation=landscape"
                      alt="Professional painters Melkbosstrand coastal house painting services Cape Town"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Comprehensive Painting Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with quality materials and expert craftsmanship for coastal properties.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`Painters Melkbosstrand coastal painting project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding coastal areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
